import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getBusinesses, deleteBusiness, setFeatured, setSponsored, setOurPick } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Spinner } from '../../components/ui/Spinner'
import { Toggle } from '../../components/ui/Toggle'
import { PageHeader } from '../../components/ui/PageHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmModal } from '../../components/ui/ConfirmModal'

export default function BusinessList() {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [confirm, setConfirm] = useState(null) // { id, name }
  const qc = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['businesses', page, search],
    queryFn: () => getBusinesses({ page, pageSize: 20, search }),
  })

  const deleteMut = useMutation({
    mutationFn: deleteBusiness,
    onSuccess: () => {
      qc.invalidateQueries(['businesses'])
      qc.invalidateQueries(['stats'])
      toast.success('Business deleted')
      setConfirm(null)
    },
    onError: (e) => { toast.error(e.message); setConfirm(null) },
  })

  const toggleMut = useMutation({
    mutationFn: ({ fn, id, value }) => fn(id, value),
    onSuccess: () => { qc.invalidateQueries(['businesses']); qc.invalidateQueries(['stats']) },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="Businesses"
        description="Manage all business listings"
        action={<Link to="/businesses/new"><Button><i className="ti ti-plus" /> New Business</Button></Link>}
      />

      <div className="mb-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <i className="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white"
            placeholder="Search businesses..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1) }}
          />
        </div>
        {data && <p className="text-sm text-gray-500">{data.total} results</p>}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.items?.length ? (
        <EmptyState icon="ti-building-store" title="No businesses found"
          description={search ? 'No results match your search.' : 'Add your first business to get started.'}
          action={!search && <Link to="/businesses/new"><Button size="sm">Add Business</Button></Link>}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/50">
                  <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Business</th>
                  <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Category</th>
                  <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Location</th>
                  <th className="text-center px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Featured</th>
                  <th className="text-center px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Sponsored</th>
                  <th className="text-center px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Our Pick</th>
                  <th className="text-center px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.items?.map(b => (
                  <tr key={b.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {b.firstImage ? (
                          <img src={b.firstImage} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0 ring-1 ring-gray-100" />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <i className="ti ti-building-store text-gray-400 text-sm" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{b.name}</p>
                          <p className="text-xs text-gray-400">{b.phone || '—'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4"><Badge color="indigo">{b.categoryLabel}</Badge></td>
                    <td className="px-5 py-4 text-gray-500 text-xs">
                      <div className="flex items-center gap-1">
                        <i className="ti ti-map-pin text-gray-300" />{b.city}, {b.country}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div className="flex justify-center">
                        <Toggle checked={b.featured} onChange={v => toggleMut.mutate({ fn: setFeatured, id: b.id, value: v })} />
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div className="flex justify-center">
                        <Toggle checked={b.sponsored} onChange={v => toggleMut.mutate({ fn: setSponsored, id: b.id, value: v })} />
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <div className="flex justify-center">
                        <Toggle checked={b.ourPick} onChange={v => toggleMut.mutate({ fn: setOurPick, id: b.id, value: v })} />
                      </div>
                    </td>
                    <td className="px-5 py-4 text-center">
                      <Badge color={b.isActive ? 'green' : 'gray'}>{b.isActive ? 'Active' : 'Inactive'}</Badge>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/businesses/${b.id}/images`}>
                          <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600"><i className="ti ti-photo" /></Button>
                        </Link>
                        <Link to={`/businesses/${b.id}/edit`}>
                          <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600"><i className="ti ti-pencil" /></Button>
                        </Link>
                        <Button variant="ghost" size="xs" className="text-gray-400 hover:text-red-600"
                          onClick={() => setConfirm({ id: b.id, name: b.name })}>
                          <i className="ti ti-trash" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {data?.totalPages > 1 && (
            <div className="px-5 py-3.5 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-500">Page {data.page} of {data.totalPages} · {data.total} total</p>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                  <i className="ti ti-chevron-left" /> Previous
                </Button>
                <Button variant="secondary" size="sm" disabled={page === data.totalPages} onClick={() => setPage(p => p + 1)}>
                  Next <i className="ti ti-chevron-right" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => deleteMut.mutate(confirm.id)}
        title="Delete Business"
        message={`"${confirm?.name}" will be permanently deleted.`}
        loading={deleteMut.isPending}
      />
    </div>
  )
}
